import type { CubicFeetCalculationConfig } from 'contexts/serviceContext';
import { Maybe, Undef } from 'types/UtilityTypes';
import { densityToFreightClass } from 'utils/densityToFreightClass';

import {
  LTLItem,
  LTLUnitSystem,
  LTL_THRESHOLDS,
  LTL_UNIT_SYSTEMS,
} from '../types';
import { calculateCubicFeetRaw } from './ltlVolumeFromItems';
import {
  type PackingFootprint,
  packUnitsWithSkylineAndLocalImprovements,
} from './skylinePacking';

interface LTLMetrics {
  linearFeet: number;
  totalCubicFeet: number;
  totalWeight: number;
  pcf: number;
  freightClass?: number;
}

export const CUBIC_METERS_PER_CUBIC_FOOT = 0.0283168;
// Floating-point tolerance for fit checks and tie-break comparisons.
const EPSILON = 0.0001;
const MAX_PACKING_HANDLING_UNITS = 2000;
const CUBIC_INCHES_PER_CUBIC_FOOT = 1728;
export const DEFAULT_MAX_STACK_HEIGHT_INCHES = 96;

export interface ShipmentLayoutMetrics {
  totalLengthInches: number;
  maxWidthInches: number;
  maxHeightInches: number;
  // Max depth among shelves from the shelf packing heuristic (diagnostic), same pallet set.
  maxShelfDepthInches: number;
  strategy?: PackingStrategyName;
}

interface HandlingUnitInches {
  widthInches: number;
  lengthInches: number;
  heightInches: number;
}

interface VirtualStack {
  widthInches: number;
  lengthInches: number;
  heightInches: number;
}

interface StackLayer {
  heightInches: number;
  lanes: PackingLane[];
}

interface StackPlacementCandidate {
  stackIdx: number;
  layerIdx: number;
  useNewLayer: boolean;
  lanePlacement?: {
    laneIdx: number;
    openNewLane: boolean;
  };
  resultingHeightInches: number;
  addedHeightInches: number;
  resultingWidthInches: number;
  resultingLengthInches: number;
}

interface StackState extends VirtualStack {
  layers: StackLayer[];
}

interface BuildShipmentLayoutOptions {
  stackingEnabled?: boolean;
  isItemStackable?: (item: LTLItem) => boolean;
  maxStackHeightInches?: number;
  customConfig?: HeightConfig;
}

// Options shared by `calculateLTLMetrics` and trailer axis labels.
export interface ResolveQuickQuoteShipmentFootprintOptions {
  stackingEnabled?: boolean;
  isItemStackable?: (item: LTLItem) => boolean;
  maxStackHeightInches?: number;
  customConfig?: HeightConfig;
  // When set, skips `buildShipmentLayoutMetrics` (same as `calculateLTLMetrics`).
  precomputedLayout?: ShipmentLayoutMetrics;
}

export interface QuickQuoteShipmentFootprint {
  layout: ShipmentLayoutMetrics;
  // Height used with layout width/length for cubic envelope (override when configured).
  effectiveHeightInches: number;
}

// Single entry point for shipment layout + effective height so UI and metrics
// stay aligned without duplicating `buildShipmentLayoutMetrics` + `getShipmentHeight`.
export function resolveQuickQuoteShipmentFootprint(
  items: LTLItem[],
  units: LTLUnitSystem = LTL_UNIT_SYSTEMS.IMPERIAL,
  options?: ResolveQuickQuoteShipmentFootprintOptions
): QuickQuoteShipmentFootprint {
  const {
    stackingEnabled = false,
    isItemStackable,
    maxStackHeightInches,
    customConfig,
    precomputedLayout,
  } = options ?? {};

  const layout =
    precomputedLayout ??
    buildShipmentLayoutMetrics(items, units, {
      stackingEnabled,
      isItemStackable,
      maxStackHeightInches,
      customConfig,
    });

  const effectiveHeightInches = getShipmentHeight(
    layout.maxHeightInches,
    customConfig
  );

  return { layout, effectiveHeightInches };
}

interface ExpandedPackingUnit {
  widthInches: number;
  lengthInches: number;
  canRotate: boolean;
}

export type PackingStrategyName =
  | 'default_ffd'
  | 'area_ffd'
  | 'pair_first'
  | 'reverse_ffd'
  | 'width_complement';

export type HeightConfig = Pick<
  CubicFeetCalculationConfig,
  'heightThresholdInches' | 'overrideHeightInches'
>;

interface CalculateLTLMetricsOptions {
  customConfig?: HeightConfig;
  useLayoutBoundingBoxForCubicFeet?: boolean;
  stackingEnabled?: boolean;
  isItemStackable?: (item: LTLItem) => boolean;
  maxStackHeightInches?: number;
  precomputedLayout?: ShipmentLayoutMetrics;
}

function toInches(value: number, units: LTLUnitSystem): number {
  return units === LTL_UNIT_SYSTEMS.METRIC ? value / 2.54 : value;
}

function toLbs(value: number, units: LTLUnitSystem): number {
  return units === LTL_UNIT_SYSTEMS.METRIC ? value * 2.20462 : value;
}

export function sortLtlItemsForTaiPacking(
  items: LTLItem[],
  units: LTLUnitSystem
): LTLItem[] {
  return [...items].sort((a, b) => {
    const widthA = toInches(a.width ?? 0, units);
    const widthB = toInches(b.width ?? 0, units);
    const lengthA = toInches(a.length ?? 0, units);
    const lengthB = toInches(b.length ?? 0, units);
    if (Math.abs(widthB - widthA) > EPSILON) {
      return widthB - widthA;
    }
    if (Math.abs(lengthB - lengthA) > EPSILON) {
      return lengthB - lengthA;
    }
    return String(a.itemId).localeCompare(String(b.itemId));
  });
}

export function normalizeHandlingUnitsForPacking(
  handlingUnits: LTLItem['handlingUnits']
): number {
  const parsed =
    typeof handlingUnits === 'number' ? handlingUnits : Number(handlingUnits);
  if (parsed === 0) {
    return 0;
  }
  if (!Number.isFinite(parsed) || parsed < 0) {
    return 1;
  }
  return Math.min(MAX_PACKING_HANDLING_UNITS, Math.max(1, Math.floor(parsed)));
}

function calculateLinearFeetFromLengthInches(
  totalLengthInches: number
): number {
  return Math.ceil(totalLengthInches / 12);
}

function shouldApplyHeightOverride(
  maxHeightInches: number,
  config?: HeightConfig
): boolean {
  if (!config) {
    return false;
  }
  return maxHeightInches > config.heightThresholdInches;
}

export function getShipmentHeight(
  maxHeightInches: number,
  config?: HeightConfig
): number {
  if (shouldApplyHeightOverride(maxHeightInches, config)) {
    return config?.overrideHeightInches ?? maxHeightInches;
  }
  return maxHeightInches;
}

export function resolveMaxStackHeightInches(config?: HeightConfig): number {
  return config?.overrideHeightInches ?? DEFAULT_MAX_STACK_HEIGHT_INCHES;
}

function isBetterStackPlacementCandidate(
  candidate: StackPlacementCandidate,
  currentBest: Maybe<StackPlacementCandidate>
): boolean {
  if (!currentBest) {
    return true;
  }
  if (candidate.addedHeightInches < currentBest.addedHeightInches - EPSILON) {
    return true;
  }
  if (
    Math.abs(candidate.addedHeightInches - currentBest.addedHeightInches) <=
    EPSILON
  ) {
    if (
      candidate.resultingHeightInches <
      currentBest.resultingHeightInches - EPSILON
    ) {
      return true;
    }
    if (
      Math.abs(
        candidate.resultingHeightInches - currentBest.resultingHeightInches
      ) <= EPSILON
    ) {
      return (
        candidate.resultingWidthInches * candidate.resultingLengthInches <
        currentBest.resultingWidthInches * currentBest.resultingLengthInches -
          EPSILON
      );
    }
  }
  return false;
}

function evaluatePlacementInExistingLayer(
  stack: StackState,
  stackIdx: number,
  layerIdx: number,
  hu: HandlingUnitInches,
  targetWidthInches: number,
  targetLengthInches: number,
  maxStackHeightInches: number
): Maybe<StackPlacementCandidate> {
  const layer = stack.layers[layerIdx];
  const placement = chooseBestLaneForPallet(
    layer.lanes,
    hu.widthInches,
    hu.lengthInches,
    targetWidthInches
  );
  if (!placement.openNewLane && placement.laneIdx < 0) {
    return null;
  }
  const lanesAfter = layer.lanes.map((l) => ({ ...l }));
  if (placement.openNewLane) {
    lanesAfter.push({
      laneWidthInches: hu.widthInches,
      usedLengthInches: hu.lengthInches,
    });
  } else {
    lanesAfter[placement.laneIdx].usedLengthInches += hu.lengthInches;
  }
  const layerUsedLength = lanesAfter.reduce(
    (m, lane) => Math.max(m, lane.usedLengthInches),
    0
  );
  if (layerUsedLength - targetLengthInches > EPSILON) {
    return null;
  }
  const layerHeightAfter = Math.max(layer.heightInches, hu.heightInches);
  const addedHeight = layerHeightAfter - layer.heightInches;
  const stackHeightAfter = stack.heightInches + addedHeight;
  if (stackHeightAfter - maxStackHeightInches > EPSILON) {
    return null;
  }
  return {
    stackIdx,
    layerIdx,
    useNewLayer: false,
    lanePlacement: placement,
    resultingHeightInches: stackHeightAfter,
    addedHeightInches: addedHeight,
    resultingWidthInches: targetWidthInches,
    resultingLengthInches: targetLengthInches,
  };
}

function evaluateNewLayerPlacement(
  stack: StackState,
  stackIdx: number,
  hu: HandlingUnitInches,
  targetWidthInches: number,
  targetLengthInches: number,
  maxStackHeightInches: number
): Maybe<StackPlacementCandidate> {
  const stackHeightAfter = stack.heightInches + hu.heightInches;
  if (stackHeightAfter - maxStackHeightInches > EPSILON) {
    return null;
  }
  return {
    stackIdx,
    layerIdx: -1,
    useNewLayer: true,
    resultingHeightInches: stackHeightAfter,
    addedHeightInches: hu.heightInches,
    resultingWidthInches: targetWidthInches,
    resultingLengthInches: targetLengthInches,
  };
}

function greedyStackIntoVirtualHandlingUnits(
  hus: HandlingUnitInches[],
  maxStackHeightInches: number
): VirtualStack[] {
  if (!hus.length) {
    return [];
  }
  const sorted = [...hus].sort((a, b) => {
    const areaA = a.widthInches * a.lengthInches;
    const areaB = b.widthInches * b.lengthInches;
    if (areaB !== areaA) {
      // larger footprint first (better base candidates)
      return areaB - areaA;
    }

    if (b.heightInches !== a.heightInches) {
      return b.heightInches - a.heightInches;
    }

    if (b.widthInches !== a.widthInches) {
      return b.widthInches - a.widthInches;
    }
    return b.lengthInches - a.lengthInches;
  });
  const stacks: StackState[] = [];
  for (const hu of sorted) {
    let best: Maybe<StackPlacementCandidate> = null;
    for (let stackIdx = 0; stackIdx < stacks.length; stackIdx += 1) {
      const stack = stacks[stackIdx];
      const targetWidthInches = Math.max(stack.widthInches, hu.widthInches);
      const targetLengthInches = Math.max(stack.lengthInches, hu.lengthInches);
      for (let layerIdx = 0; layerIdx < stack.layers.length; layerIdx += 1) {
        const candidate = evaluatePlacementInExistingLayer(
          stack,
          stackIdx,
          layerIdx,
          hu,
          targetWidthInches,
          targetLengthInches,
          maxStackHeightInches
        );
        if (candidate && isBetterStackPlacementCandidate(candidate, best))
          best = candidate;
      }
      const newLayerCandidate = evaluateNewLayerPlacement(
        stack,
        stackIdx,
        hu,
        targetWidthInches,
        targetLengthInches,
        maxStackHeightInches
      );
      if (
        newLayerCandidate &&
        isBetterStackPlacementCandidate(newLayerCandidate, best)
      ) {
        best = newLayerCandidate;
      }
    }
    if (!best) {
      stacks.push({
        widthInches: hu.widthInches,
        lengthInches: hu.lengthInches,
        heightInches: hu.heightInches,
        layers: [
          {
            heightInches: hu.heightInches,
            lanes: [
              {
                laneWidthInches: hu.widthInches,
                usedLengthInches: hu.lengthInches,
              },
            ],
          },
        ],
      });
      continue;
    }
    const target = stacks[best.stackIdx];
    if (best.useNewLayer) {
      target.layers.push({
        heightInches: hu.heightInches,
        lanes: [
          {
            laneWidthInches: hu.widthInches,
            usedLengthInches: hu.lengthInches,
          },
        ],
      });
      target.widthInches = best.resultingWidthInches;
      target.lengthInches = best.resultingLengthInches;
      target.heightInches = best.resultingHeightInches;
      continue;
    }
    const layer = target.layers[best.layerIdx];
    const placement = best.lanePlacement;
    if (!placement) {
      // Fallback: place as new lane in the selected layer instead of dropping HU.
      const previousLayerHeightInches = layer.heightInches;

      layer.lanes.push({
        laneWidthInches: hu.widthInches,
        usedLengthInches: hu.lengthInches,
      });

      layer.heightInches = Math.max(layer.heightInches, hu.heightInches);
      target.widthInches = Math.max(target.widthInches, hu.widthInches);
      target.lengthInches = Math.max(target.lengthInches, hu.lengthInches);
      target.heightInches += Math.max(
        layer.heightInches - previousLayerHeightInches,
        0
      );

      continue;
    }

    if (placement.openNewLane) {
      layer.lanes.push({
        laneWidthInches: hu.widthInches,
        usedLengthInches: hu.lengthInches,
      });
    } else if (placement.laneIdx >= 0) {
      layer.lanes[placement.laneIdx].usedLengthInches += hu.lengthInches;
    } else {
      layer.lanes.push({
        laneWidthInches: hu.widthInches,
        usedLengthInches: hu.lengthInches,
      });
    }

    layer.heightInches = Math.max(layer.heightInches, hu.heightInches);
    target.widthInches = best.resultingWidthInches;
    target.lengthInches = best.resultingLengthInches;
    target.heightInches = best.resultingHeightInches;
  }
  return stacks.map(({ widthInches, lengthInches, heightInches }) => ({
    widthInches,
    lengthInches,
    heightInches,
  }));
}

function calculateShipmentCubicFeet(
  shipmentLengthInches: number,
  shipmentWidthInches: number,
  shipmentHeightInches: number
): number {
  if (
    shipmentLengthInches <= 0 ||
    shipmentWidthInches <= 0 ||
    shipmentHeightInches <= 0
  ) {
    return 0;
  }

  return (
    (shipmentLengthInches * shipmentWidthInches * shipmentHeightInches) /
    CUBIC_INCHES_PER_CUBIC_FOOT
  );
}

interface PackingLane {
  laneWidthInches: number;
  usedLengthInches: number;
}

function chooseBestLaneForPallet(
  lanes: PackingLane[],
  palletWidthInches: number,
  palletLengthInches: number,
  trailerWidthInches: number
): { laneIdx: number; openNewLane: boolean } {
  const currentMaxLength = lanes.reduce(
    (m, lane) => Math.max(m, lane.usedLengthInches),
    0
  );
  const usedWidthInches = lanes.reduce(
    (sum, lane) => sum + lane.laneWidthInches,
    0
  );
  const remainingWidthInches = trailerWidthInches - usedWidthInches;

  let bestLaneIdx = -1;
  let bestMaxLength = Number.POSITIVE_INFINITY;
  let bestLanePostLength = Number.POSITIVE_INFINITY;
  let bestWaste = Number.POSITIVE_INFINITY;

  for (let i = 0; i < lanes.length; i += 1) {
    const lane = lanes[i];
    if (palletWidthInches - lane.laneWidthInches > EPSILON) {
      continue;
    }

    const postLength = lane.usedLengthInches + palletLengthInches;
    const postMaxLength = Math.max(currentMaxLength, postLength);
    const waste = lane.laneWidthInches - palletWidthInches;

    const betterMax = postMaxLength < bestMaxLength - EPSILON;
    const tieMax = Math.abs(postMaxLength - bestMaxLength) <= EPSILON;
    const betterLength = postLength < bestLanePostLength - EPSILON;
    const tieLength = Math.abs(postLength - bestLanePostLength) <= EPSILON;
    const betterWaste = waste < bestWaste - EPSILON;

    if (
      betterMax ||
      (tieMax && betterLength) ||
      (tieMax && tieLength && betterWaste)
    ) {
      bestLaneIdx = i;
      bestMaxLength = postMaxLength;
      bestLanePostLength = postLength;
      bestWaste = waste;
    }
  }

  const canOpenNewLane = palletWidthInches - remainingWidthInches <= EPSILON;
  if (canOpenNewLane) {
    const postMaxLength = Math.max(currentMaxLength, palletLengthInches);
    const postLength = palletLengthInches;
    const betterMax = postMaxLength < bestMaxLength - EPSILON;
    const tieMax = Math.abs(postMaxLength - bestMaxLength) <= EPSILON;
    const betterLength = postLength < bestLanePostLength - EPSILON;

    if (bestLaneIdx < 0 || betterMax || (tieMax && betterLength)) {
      return { laneIdx: -1, openNewLane: true };
    }
  }

  return { laneIdx: bestLaneIdx, openNewLane: false };
}

function sortExpandedPackingUnits(
  units: ExpandedPackingUnit[]
): ExpandedPackingUnit[] {
  return [...units].sort((a, b) => {
    const maxA = Math.max(a.lengthInches, a.widthInches);
    const maxB = Math.max(b.lengthInches, b.widthInches);

    if (Math.abs(maxB - maxA) > EPSILON) {
      return maxB - maxA;
    }

    const areaA = a.lengthInches * a.widthInches;
    const areaB = b.lengthInches * b.widthInches;

    if (Math.abs(areaB - areaA) > EPSILON) {
      return areaB - areaA;
    }

    if (Math.abs(b.lengthInches - a.lengthInches) > EPSILON) {
      return b.lengthInches - a.lengthInches;
    }

    return b.widthInches - a.widthInches;
  });
}

function sortExpandedPackingUnitsByArea(
  units: ExpandedPackingUnit[]
): ExpandedPackingUnit[] {
  return [...units].sort((a, b) => {
    const areaA = a.lengthInches * a.widthInches;
    const areaB = b.lengthInches * b.widthInches;

    if (Math.abs(areaB - areaA) > EPSILON) {
      return areaB - areaA;
    }

    const maxA = Math.max(a.lengthInches, a.widthInches);
    const maxB = Math.max(b.lengthInches, b.widthInches);

    if (Math.abs(maxB - maxA) > EPSILON) {
      return maxB - maxA;
    }

    if (Math.abs(b.lengthInches - a.lengthInches) > EPSILON) {
      return b.lengthInches - a.lengthInches;
    }

    return b.widthInches - a.widthInches;
  });
}

// Multi-run ordering strategies stay bounded/deterministic and help avoid
// single-order bias while keeping skyline packing logic unchanged.
function prioritizeFullTrailerWidthPairFirst(
  units: ExpandedPackingUnit[],
  trailerWidthInches: number
): ExpandedPackingUnit[] {
  if (units.length < 2) {
    return units;
  }
  const depthLeading = units[0].lengthInches;
  for (let i = 0; i < units.length; i += 1) {
    for (let j = i + 1; j < units.length; j += 1) {
      const sum = units[i].widthInches + units[j].widthInches;
      if (Math.abs(sum - trailerWidthInches) > EPSILON) {
        continue;
      }
      const pairFirstRowDepth = Math.max(
        units[i].lengthInches,
        units[j].lengthInches
      );
      if (pairFirstRowDepth > depthLeading + EPSILON) {
        continue;
      }
      const rest = units.filter((_, k) => k !== i && k !== j);
      return [units[i], units[j], ...rest];
    }
  }
  return units;
}

function applyWidthComplementOrdering(
  units: ExpandedPackingUnit[],
  trailerWidthInches: number
): ExpandedPackingUnit[] {
  if (units.length <= 1) {
    return units;
  }

  const used = new Array(units.length).fill(false);
  const ordered: ExpandedPackingUnit[] = [];

  for (let i = 0; i < units.length; i += 1) {
    if (used[i]) continue;
    const unit = units[i];
    used[i] = true;
    ordered.push(unit);

    const remainingWidth = trailerWidthInches - unit.widthInches;
    if (remainingWidth <= EPSILON) {
      continue;
    }

    let complementIdx = -1;
    for (let j = i + 1; j < units.length; j += 1) {
      if (used[j]) continue;
      const candidate = units[j];
      const widthMatches =
        Math.abs(candidate.widthInches - remainingWidth) <= EPSILON;
      const rotatedMatches =
        candidate.canRotate &&
        Math.abs(candidate.lengthInches - remainingWidth) <= EPSILON;
      if (widthMatches || rotatedMatches) {
        complementIdx = j;
        break;
      }
    }

    if (complementIdx >= 0) {
      used[complementIdx] = true;
      ordered.push(units[complementIdx]);
    }
  }

  return ordered;
}

function buildPackingRuns(
  sortedUnits: ExpandedPackingUnit[],
  trailerWidthInches: number
): Array<{
  strategy: PackingStrategyName;
  orderedUnits: ExpandedPackingUnit[];
}> {
  const runs = [
    { strategy: 'default_ffd' as const, orderedUnits: sortedUnits },
    {
      strategy: 'area_ffd' as const,
      orderedUnits: sortExpandedPackingUnitsByArea(sortedUnits),
    },
    {
      strategy: 'pair_first' as const,
      orderedUnits: prioritizeFullTrailerWidthPairFirst(
        sortedUnits,
        trailerWidthInches
      ),
    },
    {
      strategy: 'reverse_ffd' as const,
      orderedUnits: [...sortedUnits].reverse(),
    },
    {
      strategy: 'width_complement' as const,
      orderedUnits: applyWidthComplementOrdering(
        sortedUnits,
        trailerWidthInches
      ),
    },
  ];

  const deduped: Array<{
    strategy: PackingStrategyName;
    orderedUnits: ExpandedPackingUnit[];
  }> = [];
  const seen = new Set<string>();
  for (const run of runs) {
    const key = run.orderedUnits
      .map((u) => `${u.widthInches}x${u.lengthInches}`)
      .join('|');
    if (seen.has(key)) continue;
    seen.add(key);
    deduped.push(run);
  }
  return deduped;
}

function isBetterPackingScore(
  candidate: { linearFeet: number; cubicFootprintScore: number; pcf: number },
  best: { linearFeet: number; cubicFootprintScore: number; pcf: number },
  epsilon: number
): boolean {
  if (candidate.linearFeet !== best.linearFeet) {
    return candidate.linearFeet < best.linearFeet;
  }
  if (candidate.cubicFootprintScore < best.cubicFootprintScore - epsilon) {
    return true;
  }
  if (candidate.cubicFootprintScore > best.cubicFootprintScore + epsilon) {
    return false;
  }
  if (candidate.pcf > best.pcf + epsilon) {
    return true;
  }
  if (candidate.pcf < best.pcf - epsilon) {
    return false;
  }
  return false;
}

// Linear feet calculation for this TMS.
// Uses shipment-level first-fit-decreasing packing across all item HUs.
export function calculateLinearFeet(
  items: LTLItem[],
  units: LTLUnitSystem = LTL_UNIT_SYSTEMS.IMPERIAL,
  stackingEnabled = false
): number {
  const shipmentLayout = buildShipmentLayoutMetrics(items, units, {
    stackingEnabled,
  });

  return calculateLinearFeetFromLengthInches(shipmentLayout.totalLengthInches);
}

interface PreparedLayoutInputs {
  expandedUnits: ExpandedPackingUnit[];
  inTrailerHusForStacking: HandlingUnitInches[];
  overflowLengthInches: number;
  maxHeightInches: number;
}

interface PackedRunSelection {
  packedFootprint: PackingFootprint;
  strategy?: PackingStrategyName;
}

function emptyShipmentLayoutMetrics(): ShipmentLayoutMetrics {
  return {
    totalLengthInches: 0,
    maxWidthInches: 0,
    maxHeightInches: 0,
    maxShelfDepthInches: 0,
  };
}

function prepareLayoutInputs(
  items: LTLItem[],
  units: LTLUnitSystem,
  trailerWidthInches: number,
  stackingEnabled: boolean,
  isItemStackable?: (item: LTLItem) => boolean
): PreparedLayoutInputs {
  const expandedUnits: ExpandedPackingUnit[] = [];
  const inTrailerHusForStacking: HandlingUnitInches[] = [];
  let overflowLengthInches = 0;
  let maxHeightInches = 0;

  const orderedItems = sortLtlItemsForTaiPacking(items, units);
  for (const item of orderedItems) {
    const length = item.length ?? 0;
    const width = item.width ?? 0;
    const height = item.height;
    const handlingUnits = normalizeHandlingUnitsForPacking(item.handlingUnits);

    if (
      length <= 0 ||
      width <= 0 ||
      !Number.isFinite(length) ||
      !Number.isFinite(width)
    ) {
      continue;
    }

    if (handlingUnits <= 0) {
      continue;
    }

    const lengthInches = toInches(length, units);
    const widthInches = toInches(width, units);
    const hasValidHeight = typeof height === 'number' && height > 0;
    let heightInches = 0;
    if (hasValidHeight) {
      heightInches = toInches(height, units);
      if (!Number.isFinite(heightInches)) {
        heightInches = 0;
      }
    }

    // Non-stacking packing keeps orientation fixed for base metrics; if unit width
    // exceeds trailer width, it is unplaceable in skyline and must be overflowed.
    const cannotFitTrailerWidth = widthInches - trailerWidthInches > EPSILON;
    const invalidConvertedDimensions =
      lengthInches <= 0 ||
      widthInches <= 0 ||
      !Number.isFinite(lengthInches) ||
      !Number.isFinite(widthInches);

    if (invalidConvertedDimensions) {
      continue;
    }

    if (cannotFitTrailerWidth) {
      if (hasValidHeight && heightInches > 0) {
        maxHeightInches = Math.max(maxHeightInches, heightInches);
      }
      overflowLengthInches += lengthInches * handlingUnits;
      continue;
    }

    const itemStackingEnabled =
      stackingEnabled && (isItemStackable?.(item) ?? true);

    if (!itemStackingEnabled && hasValidHeight && heightInches > 0) {
      maxHeightInches = Math.max(maxHeightInches, heightInches);
    }

    if (itemStackingEnabled) {
      for (let i = 0; i < handlingUnits; i += 1) {
        inTrailerHusForStacking.push({
          widthInches,
          lengthInches,
          heightInches,
        });
      }
      continue;
    }

    for (let i = 0; i < handlingUnits; i += 1) {
      // Baseline packing keeps orientation fixed; side-load handles rotation separately.
      expandedUnits.push({
        widthInches,
        lengthInches,
        canRotate: false,
      });
    }
  }

  return {
    expandedUnits,
    inTrailerHusForStacking,
    overflowLengthInches,
    maxHeightInches,
  };
}

function applyStackingIfEnabled(
  expandedUnits: ExpandedPackingUnit[],
  inTrailerHusForStacking: HandlingUnitInches[],
  maxHeightInches: number,
  stackingEnabled: boolean,
  maxStackHeightInches: number
): { expandedUnits: ExpandedPackingUnit[]; maxHeightInches: number } {
  if (!stackingEnabled || inTrailerHusForStacking.length === 0) {
    return { expandedUnits, maxHeightInches };
  }

  const virtualStacks = greedyStackIntoVirtualHandlingUnits(
    inTrailerHusForStacking,
    maxStackHeightInches
  );
  const maxVirtualHeight = virtualStacks.reduce(
    (m, v) => Math.max(m, v.heightInches),
    0
  );
  const nextMaxHeight = Math.max(maxHeightInches, maxVirtualHeight);
  for (const v of virtualStacks) {
    // Virtual stacks also stay fixed-orientation in baseline skyline packing.
    expandedUnits.push({
      widthInches: v.widthInches,
      lengthInches: v.lengthInches,
      canRotate: false,
    });
  }
  return { expandedUnits, maxHeightInches: nextMaxHeight };
}

function selectBestPackedRun(
  sortedUnits: ExpandedPackingUnit[],
  trailerWidthInches: number,
  overflowLengthInches: number,
  overflowMaxWidthInches: number,
  maxHeightInches: number,
  totalWeightLbs: number
): PackedRunSelection {
  const packingRuns = buildPackingRuns(sortedUnits, trailerWidthInches);

  let bestPackingFootprint: Maybe<PackingFootprint> = null;
  let bestStrategy: Undef<PackingStrategyName>;
  let bestEval: Maybe<{
    linearFeet: number;
    cubicFootprintScore: number;
    pcf: number;
  }> = null;

  for (const run of packingRuns) {
    const packedFootprint = packUnitsWithSkylineAndLocalImprovements(
      run.orderedUnits,
      trailerWidthInches
    );
    const totalLengthInches =
      packedFootprint.packedLengthInches + overflowLengthInches;
    const maxWidthInches = Math.max(
      packedFootprint.packedWidthInches,
      overflowMaxWidthInches
    );
    const linearFeet = calculateLinearFeetFromLengthInches(totalLengthInches);
    const cubicFootprintScore =
      totalLengthInches * maxWidthInches * Math.max(maxHeightInches, 0);
    const pcf =
      cubicFootprintScore > EPSILON
        ? totalWeightLbs / (cubicFootprintScore / CUBIC_INCHES_PER_CUBIC_FOOT)
        : 0;

    const candidateEval = {
      linearFeet,
      cubicFootprintScore,
      pcf,
    };
    if (!bestEval || isBetterPackingScore(candidateEval, bestEval, EPSILON)) {
      bestEval = candidateEval;
      bestPackingFootprint = packedFootprint;
      bestStrategy = run.strategy;
    }
  }

  return {
    packedFootprint:
      bestPackingFootprint ??
      packUnitsWithSkylineAndLocalImprovements([], trailerWidthInches),
    strategy: bestStrategy,
  };
}

// Source of truth for shipment-level math: all items are packed into one layout,
// then linear feet / cubic feet / PCF are derived from that combined footprint.
export function buildShipmentLayoutMetrics(
  items: LTLItem[],
  units: LTLUnitSystem = LTL_UNIT_SYSTEMS.IMPERIAL,
  options?: BuildShipmentLayoutOptions
): ShipmentLayoutMetrics {
  const stackingEnabled = options?.stackingEnabled ?? false;
  const isItemStackable = options?.isItemStackable;
  const customConfig = options?.customConfig;
  const maxStackHeightInches =
    options?.maxStackHeightInches ?? resolveMaxStackHeightInches(customConfig);

  if (!items?.length) {
    return emptyShipmentLayoutMetrics();
  }

  const trailerWidthInches = LTL_THRESHOLDS.TRAILER_WIDTH_INCHES;
  const prepared = prepareLayoutInputs(
    items,
    units,
    trailerWidthInches,
    stackingEnabled,
    isItemStackable
  );
  const withStacking = applyStackingIfEnabled(
    prepared.expandedUnits,
    prepared.inTrailerHusForStacking,
    prepared.maxHeightInches,
    stackingEnabled,
    maxStackHeightInches
  );

  const sortedUnits = sortExpandedPackingUnits(withStacking.expandedUnits);
  const totalWeightLbs = calculateTotalWeightForItems(items, units);
  const overflowMaxWidthInches =
    prepared.overflowLengthInches > 0 ? trailerWidthInches : 0;
  const strategySelectionHeightInches = getShipmentHeight(
    withStacking.maxHeightInches,
    customConfig
  );
  const bestRun = selectBestPackedRun(
    sortedUnits,
    trailerWidthInches,
    prepared.overflowLengthInches,
    overflowMaxWidthInches,
    strategySelectionHeightInches,
    totalWeightLbs
  );
  const packedFootprint = bestRun.packedFootprint;
  const totalLengthInches =
    packedFootprint.packedLengthInches + prepared.overflowLengthInches;
  const maxWidthInches = Math.max(
    packedFootprint.packedWidthInches,
    overflowMaxWidthInches
  );

  return {
    totalLengthInches,
    maxWidthInches,
    maxHeightInches: withStacking.maxHeightInches,
    maxShelfDepthInches: packedFootprint.maxShelfDepthInches ?? 0,
    strategy: bestRun.strategy,
  };
}

export function calculateTotalPieces(items: LTLItem[]): number {
  if (!items?.length) {
    return 0;
  }

  return items.reduce((sum, item) => {
    const pieces = item.pieces ?? 0;
    return sum + (pieces > 0 ? pieces : 0);
  }, 0);
}

export function calculateTotalHandlingUnits(items: LTLItem[]): number {
  if (!items?.length) {
    return 0;
  }

  return items.reduce((sum, item) => {
    const handlingUnits = item.handlingUnits ?? 0;
    return sum + (handlingUnits > 0 ? handlingUnits : 0);
  }, 0);
}

// Calculates total weight for all line items.
// Assumes each item's weight already represents the full line item total.
export function calculateTotalWeightForItems(
  items: LTLItem[],
  units: LTLUnitSystem = LTL_UNIT_SYSTEMS.IMPERIAL
): number {
  if (!items?.length) {
    return 0;
  }

  return items.reduce((sum, item) => {
    const weight = item.weight ?? 0;
    const handlingUnits = item.handlingUnits ?? 1;

    if (weight <= 0 || handlingUnits <= 0) {
      return sum;
    }

    return sum + toLbs(weight, units);
  }, 0);
}

// Calculates freight class based on PCF using the shared NMFC 2025 density scale.
export function calculateFreightClass(pcf: number): number {
  return Number(densityToFreightClass(pcf));
}

// Given a target total cubic feet value, adjusts an item's dimensions so that its
// volume matches the requested cubic feet (respecting handling units and units).
export function adjustItemDimensionsForCubicFeet(
  item: LTLItem,
  units: LTLUnitSystem,
  targetTotalCubicFeet: number
): Pick<LTLItem, 'length' | 'width' | 'height'> {
  if (
    !item ||
    !Number.isFinite(targetTotalCubicFeet) ||
    targetTotalCubicFeet <= 0
  ) {
    return {
      length: item.length,
      width: item.width,
      height: item.height,
    };
  }

  const handlingUnits = Math.max(1, Number(item.handlingUnits) || 1);

  const volumePerHandlingUnitFt3 = targetTotalCubicFeet / handlingUnits;
  if (
    !Number.isFinite(volumePerHandlingUnitFt3) ||
    volumePerHandlingUnitFt3 <= 0
  ) {
    return {
      length: item.length,
      width: item.width,
      height: item.height,
    };
  }

  // o: base edge in feet from cube root of volume per HU
  const baseEdgeFt = Math.cbrt(volumePerHandlingUnitFt3);

  // Width in feet, capped at 4 ft
  const widthFt = baseEdgeFt > 4 ? 4 : baseEdgeFt;

  // Length in feet, also capped at 4 ft
  let lengthFt = Math.sqrt(volumePerHandlingUnitFt3 / widthFt);
  if (lengthFt > 4) {
    lengthFt = 4;
  }

  // Height in feet, with an 8 ft cap; adjust length accordingly if needed
  let heightFt = volumePerHandlingUnitFt3 / (widthFt * lengthFt);
  if (heightFt > 8) {
    heightFt = 8;
    lengthFt = volumePerHandlingUnitFt3 / (widthFt * heightFt);
  }

  // Convert from feet to the current units (inches or centimeters)
  const unitMultiplier =
    units === LTL_UNIT_SYSTEMS.METRIC
      ? 30.48 // 1 ft in cm
      : 12; // 1 ft in inches

  const width = widthFt * unitMultiplier;
  const length = lengthFt * unitMultiplier;
  const height = heightFt * unitMultiplier;

  const widthRounded = Number(width.toFixed(2));
  const lengthRounded = Number(length.toFixed(2));
  const heightRounded = Number(height.toFixed(2));

  return {
    length: lengthRounded,
    width: widthRounded,
    height: heightRounded,
  };
}

// Calculates all LTL metrics including PCF.
// PCF Formula: total weight (lbs) / total volume (ft^3)
export function calculateLTLMetrics(
  items: LTLItem[],
  units: LTLUnitSystem = LTL_UNIT_SYSTEMS.IMPERIAL,
  options: CalculateLTLMetricsOptions = {}
): LTLMetrics {
  const {
    customConfig,
    useLayoutBoundingBoxForCubicFeet = true,
    stackingEnabled = false,
    isItemStackable,
    maxStackHeightInches,
    precomputedLayout,
  } = options;
  const { layout, effectiveHeightInches: shipmentHeightInches } =
    resolveQuickQuoteShipmentFootprint(items, units, {
      stackingEnabled,
      isItemStackable,
      maxStackHeightInches,
      customConfig,
      precomputedLayout,
    });
  const shipmentLengthInches = layout.totalLengthInches;
  const shipmentWidthInches = layout.maxWidthInches;
  const linearFeet = calculateLinearFeetFromLengthInches(shipmentLengthInches);

  const totalCubicFeet = useLayoutBoundingBoxForCubicFeet
    ? calculateShipmentCubicFeet(
        shipmentLengthInches,
        shipmentWidthInches,
        shipmentHeightInches
      )
    : calculateCubicFeetRaw(items, units, customConfig);

  const totalWeight = calculateTotalWeightForItems(items, units);

  // PCF: weight (lbs) / volume (ft^3). Use same volume as Total Cubic Feet (override or raw per customConfig).
  const pcf =
    totalCubicFeet > 0 && totalWeight > 0 ? totalWeight / totalCubicFeet : 0;

  // Determine freight class
  let freightClass: Undef<number>;
  if (pcf > 0) {
    freightClass = calculateFreightClass(pcf);
  } else if (totalCubicFeet > 0 && totalWeight === 0) {
    // Default to lowest class when dimensions exist but no weight
    freightClass = 400;
  }

  return {
    linearFeet,
    totalCubicFeet,
    totalWeight,
    pcf,
    freightClass,
  };
}

export function getTotalWeightDisplay(
  totalWeightLbs: number,
  units: LTLUnitSystem
): number {
  if (totalWeightLbs <= 0) {
    return 0;
  }

  return units === LTL_UNIT_SYSTEMS.METRIC
    ? totalWeightLbs / 2.20462
    : totalWeightLbs;
}

export {
  buildDerivedItemsForSideLoad,
  calculateBestSideLoadOpportunity,
  SIDELOAD_MIN_CUBIC_FEET_IMPROVEMENT,
  SIDELOAD_MIN_LINEAR_FEET_IMPROVEMENT,
  sideLoadMaxShelfDepthIncreaseInches,
} from './sideLoading';
export type {
  SideLoadOpportunityResult,
  SideLoadSuggestionItem,
} from './sideLoading';
